import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  Button,
  Spinner,
  Divider,
  Flex,
  Heading,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getAffectedPlaylists } from '../../../api/libraryApi';

interface ReprocessConfirmDialogProps {
  trackId: string;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isConfirming?: boolean;
}

export function ReprocessConfirmDialog({
  trackId,
  open,
  onClose,
  onConfirm,
  isConfirming,
}: ReprocessConfirmDialogProps) {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['affected-playlists', trackId],
    queryFn: () => getAffectedPlaylists(trackId),
    enabled: open,
  });

  return (
    <Modal isOpen={open} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent data-testid="reprocess-confirm-dialog" maxW="480px" borderRadius="15px">
        <ModalHeader>
          <Heading as="h3" size="md">{t('admin.library.reprocess.confirmTitle')}</Heading>
        </ModalHeader>
        <ModalBody>
          <VStack spacing={3} align="stretch">
            <Text>{t('admin.library.reprocess.confirmMessage')}</Text>

            {isLoading ? (
              <Flex justify="center" p={2}>
                <Spinner size="sm" />
              </Flex>
            ) : data && data.totalCount > 0 ? (
              <VStack spacing={2} align="stretch">
                <Text fontWeight="bold" color="orange.500">
                  {t('admin.library.reprocess.affectedPlaylists')}
                </Text>
                {data.affectedPlaylists.map((playlist) => (
                  <Flex
                    key={playlist.playlistId}
                    p={2}
                    bg="gray.100"
                    borderRadius="md"
                    justify="space-between"
                  >
                    <Text>{playlist.playlistName}</Text>
                    <Text fontSize={12} color="gray.600">
                      {playlist.playlistType}
                    </Text>
                  </Flex>
                ))}
              </VStack>
            ) : (
              <Text color="gray.600">
                {t('admin.library.reprocess.noAffectedPlaylists')}
              </Text>
            )}
          </VStack>
        </ModalBody>

        <Divider />

        <ModalFooter>
          <Flex justify="flex-end" gap={2}>
            <Button
              variant="outline"
              onClick={onClose}
              isDisabled={isConfirming}
              data-testid="reprocess-cancel-button"
            >
              {t('admin.library.reprocess.cancel')}
            </Button>
            <Button
              colorScheme="red"
              onClick={onConfirm}
              isDisabled={isLoading || isConfirming}
              data-testid="reprocess-confirm-button"
            >
              {isConfirming ? <Spinner size="sm" /> : t('admin.library.reprocess.confirm')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
